import React from "react";

import { useGetFarmDocScanSummary } from "utils";

import { DocScanYearSummary } from "models";

import { SkeletonWrapper, Typography } from "components";

import { AutomationCard } from "pages/admin/verifications/components/automations/AutomationCard";
import { formatSummaryCount, formatSummaryPercent } from "pages/admin/verifications/utils/docScanEvidence";

type SummaryColumn = {
  key: keyof Omit<DocScanYearSummary, "year">;
  title: string;
  format: (value: number) => string;
  className?: string;
};

const SUMMARY_COLUMNS: SummaryColumn[] = [
  { key: "docs", title: "Docs", format: formatSummaryCount, className: "muted" },
  { key: "matchedPct", title: "Matched", format: formatSummaryPercent, className: "strong" },
  { key: "cropOk", title: "Crop ok", format: formatSummaryCount },
  { key: "cropWrong", title: "Crop wrong", format: formatSummaryCount, className: "danger" },
  { key: "sizeDiff", title: "Size diff", format: formatSummaryCount, className: "danger" },
  { key: "splits", title: "Splits", format: formatSummaryCount, className: "warning" },
];

const getYearRange = (years: DocScanYearSummary[]) => {
  const yearValues = years.map(({ year }) => year);
  const firstYear = Math.min(...yearValues);
  const lastYear = Math.max(...yearValues);
  return firstYear === lastYear ? `${firstYear}` : `${firstYear}–${lastYear}`;
};

const SummaryFooter = ({ years }: { years: DocScanYearSummary[] }) => (
  <>
    <div>
      <b>Matched</b> = fields with a CAP parcel found · <b>Crop ok/wrong</b> = document crop vs platform · <b>Size diff</b> = area differs by more than 10%
      (fields &lt; 1 ha), 5% (1-10 ha) or 1% (&gt; 10 ha)
    </div>
    {years.length > 0 && <div>CAP applications {getYearRange(years)}, one row per year across all of that year&apos;s documents.</div>}
  </>
);

export const DocScanSummaryCard = ({ farmId }: { farmId: string }) => {
  const { data, isLoading } = useGetFarmDocScanSummary({ farmId });
  const years = data?.years ?? [];

  return (
    <AutomationCard iconName="tabler/file-check" title="Document scanning" footer={<SummaryFooter years={years} />}>
      <SkeletonWrapper isLoading={isLoading} type="paragraph" rows={3}>
        {years.length === 0 ? (
          <Typography variant="paragraph-s" color="grey-600">
            No CAP documents scanned yet
          </Typography>
        ) : (
          <table className="doc-scan-summary-table">
            <thead>
              <tr>
                <th>Year</th>
                {SUMMARY_COLUMNS.map(({ key, title }) => (
                  <th key={key}>{title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {years.map((yearSummary) => (
                <tr key={yearSummary.year}>
                  <td>{yearSummary.year}</td>
                  {SUMMARY_COLUMNS.map(({ key, format, className }) => (
                    <td key={key} className={yearSummary[key] ? className : undefined}>
                      {format(yearSummary[key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SkeletonWrapper>
    </AutomationCard>
  );
};
